import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";
import { outcomeGamesStatusOptions, outcomeStatusOptions } from "../shared";

type OutcomeGameSlice = {
  status?: number;
};

type SupplementalName = {
  supplementalNameTypeId: string | number;
  name?: string;
  eventMarketOutcomeName?: string;
};

type OutcomeGridRow = {
  sportCode?: string;
  status?: number;
  prematchGame?: OutcomeGameSlice;
  liveGame?: OutcomeGameSlice;
  feeds?: { name: string }[];
  supplementalNames?: SupplementalName[];
  position?: number | string | null;
};

function gameStatusLabel(status: number | undefined) {
  const match = outcomeGamesStatusOptions.find((option) => option.value === status);
  return t(match?.title ?? "");
}

function gameStatusIcon(status: number | undefined) {
  const match = outcomeGamesStatusOptions.find((option) => option.value === status);
  return {
    code: match?.icon?.code ?? "",
    color: match?.icon?.color ?? "",
  };
}

function supplementalValue(
  row: OutcomeGridRow,
  typeId: string | number,
  field: "name" | "eventMarketOutcomeName",
) {
  return (
    row.supplementalNames?.find((item) => String(item.supplementalNameTypeId) === String(typeId))?.[
      field
    ] ?? ""
  );
}

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => ({ code: `${(row as OutcomeGridRow).sportCode ?? ""}` }),
    },
  },
  {
    key: "marketId",
    defaultVisibility: true,
    title: "table_columns.market_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "marketName",
    defaultVisibility: true,
    title: "table_columns.market_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "eventMarketOutcomeName",
    defaultVisibility: true,
    title: "table_columns.event_market_outcome_name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "translatedName",
    defaultVisibility: false,
    title: "table_columns.translated_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sourceTenantName",
    defaultVisibility: false,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "translatedEventMarketOutcomeName",
    defaultVisibility: false,
    title: "table_columns.translated_event_market_outcome_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "position",
    defaultVisibility: false,
    title: "table_columns.position",
    fieldType: FieldType.InputCell,
    valueFormatter: (row: unknown) => {
      const position = (row as OutcomeGridRow).position;
      return position == null ? "" : String(position);
    },
  },
  {
    key: "prematchGameStatus",
    defaultVisibility: true,
    title: "table_columns.prematch_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => gameStatusLabel((row as OutcomeGridRow).prematchGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as OutcomeGridRow).prematchGame?.status),
    },
  },
  {
    key: "liveGameStatus",
    defaultVisibility: true,
    title: "table_columns.live_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => gameStatusLabel((row as OutcomeGridRow).liveGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as OutcomeGridRow).liveGame?.status),
    },
  },
  {
    key: "feeds",
    defaultVisibility: false,
    title: "table_columns.feeds",
    fieldType: FieldType.ArrayCell,
    valueFormatter: (row: unknown) =>
      ((row as OutcomeGridRow).feeds ?? []).map((feed) => feed.name?.split(" - ")[0] ?? ""),
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = outcomeStatusOptions.find((option) => option.value === (row as OutcomeGridRow).status);
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = outcomeStatusOptions.find(
          (option) => option.value === (row as OutcomeGridRow).status,
        );
        return { code: status?.icon?.code ?? "", color: status?.icon?.color ?? "" };
      },
    },
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [{ icon: "table-d", label: "Column view" }],
    rowActions: [
      { icon: "edit-ng", label: "Edit", onClick: (ctx) => ctx.openDialog("edit") },
      { icon: "trash-ng", label: "Delete", onClick: (ctx) => ctx.openDialog("delete") },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () =>
      getSupplementalsByEntity("OUTCOME").map((item) => ({
        key: `supplementalName_${item.id}_name`,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) => supplementalValue(row as OutcomeGridRow, item.id, "name"),
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      })),
  },
  {
    anchor: "eventMarketOutcomeName",
    createColumns: () =>
      getSupplementalsByEntity("OUTCOME").map((item) => ({
        key: `supplementalName_${item.id}_eventMarketOutcomeName`,
        title: `table_columns.${item.name.toLowerCase()}_event_market_outcome_name`,
        fieldType: FieldType.InputCell,
        defaultVisibility: false,
        valueFormatter: (row: unknown) =>
          supplementalValue(row as OutcomeGridRow, item.id, "eventMarketOutcomeName"),
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, eventMarketOutcomeName: value }],
        }),
      })),
  },
];
